import * as React from "react";
import { useState } from "react";
import { Organization } from "../../models/Organization";
import { OrganizationsRepository } from "../../repositories/OrganizationsRepository";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reveals an organization's client-enrolment secret on demand. The secret is not part of the organization list:
 * it is fetched on the first reveal, and the server records each fetch in the organization's audit log.
 */
const SecretViewer: React.FC<{ organization: Organization }> = ({
  organization,
}) => {
  const organizationsRepository = new OrganizationsRepository();
  const [secret, setSecret] = useState<string | null>(null);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toggleSecretVisibility = async () => {
    if (isSecretVisible) {
      setIsSecretVisible(false);
      return;
    }

    if (secret === null) {
      setIsLoading(true);
      try {
        setSecret(await organizationsRepository.getOrganizationSecret(organization.id));
      } catch {
        toast.error("Failed to load the organization secret");
        return;
      } finally {
        setIsLoading(false);
      }
    }

    setIsSecretVisible(true);
  };

  const copyToClipboard = () => {
    if (secret === null) return;
    navigator.clipboard.writeText(secret).then(() => {
      toast.success("Secret copied to clipboard");
    });
  };

  return (
    <div className="flex flex-row items-center gap-1.5">
      <Button
        onClick={toggleSecretVisibility}
        variant="ghost"
        size="icon-xs"
        disabled={isLoading}
        className="text-muted-foreground hover:text-foreground"
        aria-label={isSecretVisible ? "Hide secret" : "Show secret"}
      >
        {isLoading
          ? <Loader2 className="size-3.5 animate-spin" />
          : isSecretVisible ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
      </Button>
      {isSecretVisible && secret !== null ? (
        <>
          <code className="inline-flex items-center font-mono text-xs bg-muted border border-border px-2 py-1 rounded">
            {secret}
          </code>
          <Button
            onClick={copyToClipboard}
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Copy secret"
          >
            <Copy className="size-3.5" />
          </Button>
        </>
      ) : (
        <span
          onClick={toggleSecretVisibility}
          className="inline-flex items-center font-mono text-xs bg-muted border border-border px-2 py-1 rounded text-muted-foreground select-none cursor-pointer hover:border-foreground/30 transition-colors"
        >
          ●●●●●●●●
        </span>
      )}
    </div>
  );
};

export default SecretViewer;
